import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';

/**
 * SF-122 Component 3: one status banner. Never mutually exclusive across a single render — the
 * resolution logic (Component 3, implemented in `MobileThroneShell`/`DesktopThroneShell`) decides
 * which tones are active, in what order, and applies the 2-full/1-compact viewport-budget rule;
 * this component only renders whatever it's given.
 */
export type StatusBannerTone = 'protection' | 'war' | 'ceasefire' | 'dragon';

const DEFAULT_ROLE: Record<StatusBannerTone, 'alert' | 'status'> = {
  dragon: 'alert',
  war: 'alert',
  ceasefire: 'status',
  protection: 'status',
};

// gold / danger-red / amber / ember, per tone (protection/war/ceasefire/dragon). Where a tone's
// color exactly matches an existing named token (SF-125 convention: reference the token
// structurally rather than re-typing a literal that would silently desync from it), it's sourced
// from COLORS; the tint/border values have no existing token and stay literal.
const TONE_COLORS: Record<StatusBannerTone, { bg: string; border: string; text: string }> = {
  protection: { bg: '#241f10', border: '#3a3420', text: COLORS.gold },
  war:        { bg: COLORS.errorBg, border: COLORS.errorBorder, text: COLORS.negative },
  ceasefire:  { bg: '#2a2414', border: '#4a3a20', text: COLORS.warning },
  dragon:     { bg: '#241014', border: '#4a2028', text: '#ff6b3d' },
};

interface StatusBannerProps {
  tone: StatusBannerTone;
  icon: ReactNode;
  headline: string;
  detail?: string;
  role?: 'alert' | 'status';
  variant?: 'full' | 'compact';
  href?: string;
  /**
   * SF-148: opts this banner into self-announcing on DOM insertion via a native `role` HTML
   * attribute, for a consumer with no separate manual `aria-live` announcer of its own to pair
   * with (see the deviation note below for why `data-role`-only is the default). Default
   * `false` — backward compatible; `ThroneRoom`'s existing dual-banner usage never passes this
   * and is unaffected.
   */
  live?: boolean;
}

export function StatusBanner({ tone, icon, headline, detail, role, variant = 'full', href, live = false }: StatusBannerProps) {
  const colors = TONE_COLORS[tone];
  const resolvedRole = role ?? DEFAULT_ROLE[tone];

  // Deviation note: by default `resolvedRole` is exposed via `data-role`, not the native `role`
  // HTML attribute. `role="alert"`/`role="status"` are themselves implicit live regions,
  // announced on DOM insertion — applying one here as well as feeding the same headline text
  // into a separate visually-hidden `aria-live` announcer (Component 3's "live-region
  // contract", rendered by MobileThroneShell/DesktopThroneShell) would announce a newly-appeared
  // tone twice. The manual announcer already implements the spec's diff-only/no-repeat contract,
  // so it's the sole live region there; this persistent, always-visible banner is read via
  // normal top-to-bottom linear reading order instead, same as any other on-screen text.
  // `live` (SF-148) opts a standalone consumer with no manual announcer into the native `role`
  // attribute instead, so it self-announces on insertion.
  const content = (
    <div
      className="flex items-center gap-2.5 rounded-md px-3.5 py-2.5"
      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
      role={live ? resolvedRole : undefined}
      data-role={resolvedRole}
      data-tone={tone}
      data-variant={variant}
    >
      <span aria-hidden="true" style={{ color: colors.text, flexShrink: 0 }}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: colors.text }}>
          {headline}
        </div>
        {variant === 'full' && detail && (
          <div className="text-xs mt-0.5 text-game-400">{detail}</div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-game-600 rounded-md"
      >
        {content}
      </Link>
    );
  }

  return content;
}
